import { Button } from "@legal-500/legal500-design-system";
import { useCallback, useState } from "react";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import { pipelineService } from "../../../api/pipeline.service.js";
import { useTranslateFrBackfill } from "./useTranslateFrBackfill.js";
import styles from "./PipelineBrowser.module.css";

const DEFAULTS = {
  publicationId: 5773198,
  regionName: "France",
  sourceLanguage: "en",
  targetLanguage: "fr",
  batchSize: 200,
};

const FAILURE_DISPLAY_CAP = 50;
const MAX_BATCH_SIZE = 500;

function TranslateFrSection() {
  const [publicationId, setPublicationId] = useState(String(DEFAULTS.publicationId));
  const [regionName, setRegionName] = useState(DEFAULTS.regionName);
  const [sourceLanguage, setSourceLanguage] = useState(DEFAULTS.sourceLanguage);
  const [targetLanguage, setTargetLanguage] = useState(DEFAULTS.targetLanguage);
  const [batchSize, setBatchSize] = useState(String(DEFAULTS.batchSize));

  const statsParams = {
    publicationId: Number(publicationId) || 0,
    regionName,
    sourceLanguage,
    targetLanguage,
  };

  const { data, loading, error, refetch } = useQuery(
    () => pipelineService.getTranslateFrStats(statsParams),
    [],
  );

  const {
    running,
    progress,
    failures,
    error: loopError,
    start,
    stop,
  } = useTranslateFrBackfill(refetch);

  const handleStart = useCallback(() => {
    start({ ...statsParams, batchSize: Math.min(Number(batchSize) || 1, MAX_BATCH_SIZE) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, publicationId, regionName, sourceLanguage, targetLanguage, batchSize]);

  const total = data?.total ?? null;
  const shownFailures = failures.slice(0, FAILURE_DISPLAY_CAP);
  const extraFailures = failures.length - shownFailures.length;

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Translate testimonials → FR</div>

      <div className={styles.syncStateRow}>
        <input
          className={styles.filterInput}
          type="number"
          value={publicationId}
          disabled={running}
          onChange={e => setPublicationId(e.target.value)}
          placeholder="Publication ID"
          style={{ width: 140 }}
        />
        <input
          className={styles.filterInput}
          value={regionName}
          disabled={running}
          onChange={e => setRegionName(e.target.value)}
          placeholder="Region"
          style={{ width: 120 }}
        />
        <input
          className={styles.filterInput}
          value={sourceLanguage}
          disabled={running}
          onChange={e => setSourceLanguage(e.target.value)}
          placeholder="Source"
          style={{ width: 70 }}
        />
        <input
          className={styles.filterInput}
          value={targetLanguage}
          disabled={running}
          onChange={e => setTargetLanguage(e.target.value)}
          placeholder="Target"
          style={{ width: 70 }}
        />
        <input
          className={styles.filterInput}
          type="number"
          min={1}
          max={MAX_BATCH_SIZE}
          value={batchSize}
          disabled={running}
          onChange={e => setBatchSize(e.target.value)}
          placeholder="Batch size"
          style={{ width: 100 }}
        />
      </div>

      <div className={styles.syncStateRow}>
        {loading ? (
          <LoadingState message="Loading stats..." />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load stats"
            message={error.message}
            action={{ label: "Retry", onClick: () => refetch() }}
          />
        ) : (
          <>
            <span>
              Total: <strong>{data?.total ?? "-"}</strong> · Translated:{" "}
              <strong>{data?.translated ?? "-"}</strong> · Remaining:{" "}
              <strong>{data?.remaining ?? "-"}</strong>
            </span>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              disabled={running}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          </>
        )}
      </div>

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={running}
          onClick={handleStart}
        >
          {running ? `Translating… (batch ${progress.batch})` : "Start"}
        </Button>
        <Button variant="outlined" color="error" size="small" disabled={!running} onClick={stop}>
          Stop
        </Button>
      </div>

      {(running || progress.batch > 0) && (
        <div className={styles.syncStateMessage}>
          Translated {progress.processed}
          {total != null ? ` / ${total}` : ""}, skipped {progress.skipped}, remaining{" "}
          {progress.remaining ?? "-"} · failures: {progress.failed}
        </div>
      )}

      {loopError && <div className={styles.syncStateMessage}>Error: {loopError}</div>}

      {failures.length > 0 && (
        <div className={styles.contentSection}>
          <div className={styles.contentSectionTitle}>Failures ({failures.length})</div>
          {shownFailures.map((f, i) => (
            <div key={`${f.externalId}-${i}`} className={styles.syncStateMessage}>
              <strong>{f.externalId}</strong>: {f.error}
            </div>
          ))}
          {extraFailures > 0 && (
            <div className={styles.syncStateMessage}>+{extraFailures} more</div>
          )}
        </div>
      )}
    </div>
  );
}

export default TranslateFrSection;
